import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiHome, 
  FiTrendingUp, 
  FiUsers, 
  FiPackage, 
  FiBarChart3, 
  FiX,
  FiActivity,
  FiChevronLeft,
  FiChevronRight,
  FiPieChart,
  FiChevronDown,
  FiShield
} = FiIcons;

const Sidebar = ({ isOpen, onClose, isCollapsed, toggleCollapse, user }) => {
  const location = useLocation();
  const [isCustomerMenuOpen, setIsCustomerMenuOpen] = useState(true);

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FiHome,
      description: 'Overview & KPIs'
    },
    {
      name: 'Products',
      href: '/product/abiraterone-acetate',
      icon: FiPackage,
      description: 'Product Analytics'
    },
    {
      name: 'Customers',
      icon: FiUsers,
      description: 'Customer Intelligence',
      subItems: [
        { name: 'Individual Customers', href: '/customer/redoak-sourcing' },
        { name: 'Affiliation Groups', href: '/affiliation-groups' }
      ]
    },
    {
      name: 'Variance Analysis',
      href: '/variance-analysis',
      icon: FiPieChart,
      description: 'Budget vs Actuals'
    },
    {
      name: 'IQVIA Intelligence',
      href: '/iqvia',
      icon: FiBarChart3,
      description: 'Market Data'
    }
  ];

  const isItemActive = (item) => {
    if (item.href) {
      return location.pathname === item.href || 
        (item.href.includes('/product/') && location.pathname.startsWith('/product/')) ||
        (item.href.includes('/customer/') && location.pathname.startsWith('/customer/')) ||
        (item.href.includes('/affiliation-group') && location.pathname.startsWith('/affiliation-group')) ||
        (item.href.includes('/variance-analysis') && location.pathname.startsWith('/variance-analysis'));
    }
    if (item.subItems) {
      return item.subItems.some(sub => location.pathname.startsWith(sub.href));
    }
    return false;
  };

  return (
    <>
      <div
        className={`fixed left-0 top-0 h-full bg-gradient-to-b from-slate-900 to-slate-800 z-50 transition-all duration-300 ease-in-out shadow-xl
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-6 border-b border-slate-700 h-20`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiActivity} className="text-white text-xl" />
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden whitespace-nowrap">
                <h1 className="text-white font-bold text-lg">Novadoz</h1>
                <p className="text-slate-400 text-xs">Pharma Analytics</p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* User Info */}
        <div className={`p-6 border-b border-slate-700 ${isCollapsed ? 'flex justify-center' : ''}`}>
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar}
              alt={user?.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-slate-600"
            />
            {!isCollapsed && (
              <div className="overflow-hidden whitespace-nowrap">
                <p className="text-white font-medium text-sm truncate max-w-[120px]">{user?.name}</p>
                <p className="text-slate-400 text-xs truncate max-w-[120px]">{user?.role}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto max-h-[calc(100vh-250px)]">
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const isActive = isItemActive(item);
              
              if (item.subItems) {
                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => !isCollapsed && setIsCustomerMenuOpen(!isCustomerMenuOpen)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative
                        ${isActive && !isCollapsed ? 'text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}
                        ${isCollapsed ? 'justify-center' : ''}
                      `}
                    >
                      <SafeIcon 
                        icon={item.icon} 
                        className={`text-lg flex-shrink-0 ${isActive ? 'text-blue-400' : 'text-slate-400 group-hover:text-white'}`} 
                      />
                      {!isCollapsed && (
                        <div className="flex-1 text-left overflow-hidden whitespace-nowrap">
                          <p className="font-medium">{item.name}</p>
                        </div>
                      )}
                      {!isCollapsed && (
                        <SafeIcon 
                          icon={FiChevronDown} 
                          className={`text-sm transition-transform duration-200 ${isCustomerMenuOpen ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </button>
                    
                    {!isCollapsed && isCustomerMenuOpen && (
                      <div className="pl-12 space-y-1">
                        {item.subItems.map(subItem => (
                          <NavLink
                            key={subItem.name}
                            to={subItem.href}
                            className={({ isActive }) => `
                              block py-2 px-3 text-sm rounded-md transition-colors
                              ${isActive 
                                ? 'text-blue-400 font-medium' 
                                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}
                            `}
                          >
                            {subItem.name}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative
                    ${isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                  title={isCollapsed ? item.name : ''}
                >
                  <SafeIcon 
                    icon={item.icon} 
                    className={`text-lg flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} 
                  />
                  {!isCollapsed && (
                    <div className="overflow-hidden whitespace-nowrap">
                      <p className="font-medium">{item.name}</p>
                      <p className={`text-xs ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>
                        {item.description}
                      </p>
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Collapse Toggle Button */}
        <button
          onClick={toggleCollapse}
          className="hidden lg:flex absolute -right-3 top-24 w-6 h-6 bg-slate-700 border border-slate-600 rounded-full items-center justify-center text-slate-300 hover:bg-blue-600 hover:text-white hover:border-blue-500 transition-all shadow-lg z-50"
        >
          <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-xs" />
        </button>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className={`bg-slate-800 rounded-lg p-4 border border-slate-700 ${isCollapsed ? 'flex justify-center items-center' : ''}`}>
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiTrendingUp} className="text-green-400" />
                  <span className="text-white text-sm font-medium">System Status</span>
                </div>
                <p className="text-slate-400 text-xs">All systems operational</p>
              </>
            ) : (
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;